import { EarlyAccessDialog } from "@/components/EarlyAccessDialog";
import { Layout } from "@/components/Layout";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

const WhatIsDeadstockFabric = () => {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is deadstock?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Deadstock is any product, material or garment that a brand, manufacturer or supplier produced but never sold or used. That includes finished clothing, accessories, production yardage, trims and components left over from cancelled orders, overproduction, minimum-order overruns, sample runs or discontinued collections. It is brand-new, unused inventory that would otherwise sit in a warehouse or be destroyed.",
        },
      },
      {
        "@type": "Question",
        name: "Is deadstock sustainable?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Using deadstock avoids producing new goods or materials from scratch, keeps usable products out of landfill and incineration, and dramatically lowers the water, energy and carbon footprint of a garment or collection compared to virgin production.",
        },
      },
      {
        "@type": "Question",
        name: "Where can I buy deadstock in bulk?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "B2B marketplaces like HIDNstock aggregate deadstock and sorted secondhand textiles from brands, mills and sorting facilities so designers, remakers and manufacturers can source verified products and materials in the volumes they need.",
        },
      },
      {
        "@type": "Question",
        name: "What's the difference between deadstock and sorted secondhand textiles?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Deadstock is unused new product or material. Sorted secondhand textiles are post-consumer garments and fabrics that have been collected, cleaned and sorted by fibre, colour or category so they can be reused, remade or recycled at scale.",
        },
      },
    ],
  };

  return (
    <Layout>
      <Helmet>
        <title>What Is Deadstock? A Guide for Brands & Designers | HIDNstock</title>
        <meta
          name="description"
          content="Deadstock is unused product, clothing or material left over from production. Learn what it is, why it matters for circular fashion, and where to source it in bulk B2B."
        />
        <link rel="canonical" href="https://hidnstock.com/what-is-deadstock-fabric" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://hidnstock.com/what-is-deadstock-fabric" />
        <meta property="og:title" content="What Is Deadstock? A Guide for Brands & Designers" />
        <meta
          property="og:description"
          content="Deadstock is unused product, clothing or material left over from production. Learn what it is, why it matters, and where to source it in bulk B2B."
        />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <section className="py-20 md:py-28 bg-secondary/40 bg-grain border-b-4 border-foreground">
          <div className="container mx-auto px-6 lg:px-8 max-w-4xl">
            <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] text-foreground/60 mb-5">
              Glossary — 01
            </p>
            <h1
              className="font-black uppercase leading-[0.9] tracking-tight text-4xl md:text-6xl lg:text-7xl mb-6"
              style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 900 }}
            >
              What is deadstock?
            </h1>
            <p className="text-lg md:text-xl text-foreground/80 leading-relaxed max-w-3xl">
              Deadstock is any product, material or garment that was produced but never sold or used —
              finished clothing, accessories, production yardage, trims or components left over from
              cancelled orders, overproduction, minimum-order overruns, sample runs or discontinued
              collections. It's high-quality, unused inventory that would otherwise sit indefinitely
              in a warehouse or, worse, be destroyed.
            </p>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="container mx-auto px-6 lg:px-8 max-w-3xl prose prose-lg">
            <h2 className="font-display text-2xl md:text-3xl font-bold mt-0">Where does deadstock come from?</h2>
            <p className="text-foreground/80 leading-relaxed">
              Deadstock is a byproduct of how the fashion supply chain works. Mills weave more than a
              brand ends up ordering. Factories produce finished garments that never ship because a
              style is cancelled. Brands over-forecast a season, change direction mid-development or
              discontinue a collection. Manufacturers hit minimum-order quantities and end up with
              surplus. All of it — perfectly usable product or material — becomes deadstock.
            </p>

            <h2 className="font-display text-2xl md:text-3xl font-bold pt-6">Why deadstock matters for circular fashion</h2>
            <p className="text-foreground/80 leading-relaxed">
              Producing virgin goods and materials is one of the most resource-intensive steps in a
              garment's life. Every unit of deadstock reused replaces something new — cutting water,
              energy, chemical and carbon impact, and keeping usable product and material out of
              landfill and incineration. As EU textile regulation tightens around Extended Producer
              Responsibility and waste reporting, sourcing deadstock is shifting from a "nice to
              have" to a compliance advantage.
            </p>

            <h2 className="font-display text-2xl md:text-3xl font-bold pt-6">Deadstock vs. sorted secondhand textiles</h2>
            <p className="text-foreground/80 leading-relaxed">
              Deadstock is <strong>unused new product or material</strong>. Sorted secondhand textiles
              are<strong> post-consumer garments and fabrics</strong> that have been collected, cleaned
              and sorted by fibre, colour or category so they can be reused, remade or recycled at
              scale. Brands and redesigners often use both — deadstock for cut-and-sew production and
              finished goods, sorted secondhand for remake and upcycling projects.
            </p>

            <h2 className="font-display text-2xl md:text-3xl font-bold pt-6">Where to buy deadstock in bulk</h2>
            <p className="text-foreground/80 leading-relaxed">
              Historically, deadstock has been hard to source consistently — you had to know the right
              agent, mill or warehouse. B2B marketplaces like HIDNstock aggregate deadstock and sorted
              secondhand textiles from brands, mills and sorting facilities in one place, so designers,
              remakers and manufacturers can find verified products and materials in the volumes they
              actually need.
            </p>

            <div className="not-prose border-l-4 border-primary pl-6 py-5 my-10 bg-primary/5 rounded-r-lg">
              <p className="text-lg text-foreground leading-relaxed font-medium">
                Looking for specific deadstock products or materials? Post a request and we'll source
                it from our network of brands and sorting facilities.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-5">
                <EarlyAccessDialog><Button size="lg">Get started</Button></EarlyAccessDialog>
                <Link to="/request">
                  <Button size="lg" variant="outline">
                    Post a request <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>

            <h2 className="font-display text-2xl md:text-3xl font-bold pt-4">Related reading</h2>
            <ul className="text-foreground/80">
              <li><Link to="/how-it-works" className="underline">How HIDNstock works</Link></li>
              <li><Link to="/what-does-it-mean" className="underline">What does "sorted secondhand" mean?</Link></li>
              <li><Link to="/about" className="underline">About HIDNstock</Link></li>
            </ul>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default WhatIsDeadstockFabric;
